import GigShareDialog from "@/components/GigShareDialog";
import { useState, useEffect, useRef } from "react";
import { openExternalLink } from "@/lib/utils";
import { gigContactsFor } from "@/lib/gigContacts";
import ContactSheet from "@/components/ContactSheet";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import {
  Calendar, Clock, MapPin, Navigation,
  Check, Car, Train, Users, Loader2, MessageCircle, ChevronDown, X
} from "lucide-react";
import { apiClient, API } from "@/App";
import { format, parseISO, isToday, isTomorrow } from "date-fns";
import { toast } from "sonner";
import { gigLocalDate } from "@/utils/dateUtils";
import { GigBriefDesktop } from "./GigBriefDesktop";
import { useIsDesktop } from "@/hooks/useIsDesktop";
import { tokens } from "@/design-system/tokens";

// Aliases to design-system tokens. briefPal mirrors tokens.color, briefFont
// mirrors tokens.font, briefRad / briefShadow / briefBorder mirror their
// equivalents — every value the mobile card renders now resolves through
// the DS so a change in tokens.js reaches both viewport variants at once.
const briefPal    = tokens.color;
const briefFont   = tokens.font;
const briefRad    = tokens.radius;
const briefShadow = tokens.shadow;
const briefBorder = tokens.border;

// Highlights whichever of the trip's three date chips (travel/perf/return)
// matches today, so it's clear which leg of a multi-day gig you're on.
// Flips automatically at midnight since it's just a date-string comparison.
const TRIP_LEG_TODAY_BORDER = "#8FB8E8"; // pastel mid blue

function useCountdown(gig) {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(t);
  }, []);
  if (!gig?.date) return "";
  const localDateStr = gigLocalDate(gig.date);
  const parts = (localDateStr || '').split("-");
  const gd = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
  if (gig.start_time) { const [h, m] = gig.start_time.split(":").map(Number); gd.setHours(h, m); }
  else gd.setHours(20);
  const dm = Math.floor((gd - now) / 60000);
  if (dm < -180) return "";
  if (dm < 0) return "Happening now";
  if (dm < 60) return `${dm} min to showtime`;
  const dh = Math.floor(dm / 60);
  const todayStr = now.toLocaleDateString('en-CA');
  const gigDayStr = localDateStr;
  const calendarDays = Math.round(
    (new Date(gigDayStr + 'T00:00:00') - new Date(todayStr + 'T00:00:00')) / 86400000
  );
  const ts = gig.start_time || "8pm";
  if (calendarDays === 0) return `Tonight at ${ts}`;
  if (calendarDays === 1) return `Tomorrow at ${ts}`;
  if (calendarDays < 7) return `${calendarDays} day${calendarDays !== 1 ? "s" : ""}, ${dh % 24} hour${(dh % 24) !== 1 ? "s" : ""}`;
  const w = Math.floor(calendarDays / 7), r = calendarDays % 7;
  return `${w} week${w !== 1 ? "s" : ""}${r > 0 ? `, ${r} day${r !== 1 ? "s" : ""}` : ""}`;
}

// Has the traveller actually left for the airport yet? Calendar-day-only
// comparisons (today >= travelDate) flip phase at midnight on travel_date,
// hours before the real departure_time — so the brief would claim "Travelling
// to venue" while the user is still at home. When departure_time is known,
// the cutover is the real departure moment (travel_date + departure_time);
// only falls back to the midnight boundary when there's no time to anchor to.
function hasDepartedForAirport(gig) {
  if (!gig?.travel_date) return false;
  const travelDate = new Date(gig.travel_date + 'T00:00:00');
  if (!gig.departure_time) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today >= travelDate;
  }
  const [dh, dm] = gig.departure_time.split(":").map(Number);
  const departureMoment = new Date(travelDate);
  departureMoment.setHours(dh, dm, 0, 0);
  return new Date() >= departureMoment;
}

// Same idea as hasDepartedForAirport, for the return leg (return_date +
// return_time instead of travel_date + departure_time).
function hasDepartedForReturn(gig) {
  if (!gig?.return_date) return false;
  const returnDate = new Date(gig.return_date + 'T00:00:00');
  if (!gig.return_time) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today >= returnDate;
  }
  const [rh, rm] = gig.return_time.split(":").map(Number);
  const returnMoment = new Date(returnDate);
  returnMoment.setHours(rh, rm, 0, 0);
  return new Date() >= returnMoment;
}

// Soonest schedule row still ahead of right now — "HH:MM" strings sort
// chronologically as plain text, no need to parse. Once you're on site for
// a multi-day gig, your own running order (e.g. "Pick up at hotel 15:45")
// is more reliable than recomputing a drive from home, so this takes
// priority over the calculated "Leave for venue" action.
// Optional labelFilter narrows to a specific kind of row (e.g. "airport
// transfer") rather than just the soonest item — schedule rows aren't
// day-tagged yet, so on a day other than the performance day an unfiltered
// check risks matching a stale leftover row (soundcheck, doors, etc.) from
// the wrong day by time-of-day coincidence.
function nextUpcomingScheduleItem(schedule, labelFilter) {
  const nowHHMM = format(new Date(), "HH:mm");
  const upcoming = (schedule || [])
    .filter((item) => item.time && item.time > nowHHMM)
    .filter((item) => !labelFilter || labelFilter(item.label || ""))
    .sort((a, b) => a.time.localeCompare(b.time));
  return upcoming[0] || null;
}

const isAirportTransferLabel = (label) => /transfer/i.test(label);

// Ambient "you're here, doing the thing" status shown once you've arrived at
// the venue and there's nothing left on the day's Schedule ahead — fills the
// gap that used to jump straight from "arrived" to "leave for return airport".
function gigActivityLabel(gig) {
  const venue = gig?.venue_name || "the venue";
  if (gig?.gig_type === "rehearsal") return `Rehearsing at ${venue}`;
  if (gig?.gig_type === "session") return `At ${venue}`;
  return `Playing at ${venue}`;
}

export const GigBrief = ({ gig, followingGig, collaborators = [], travelMinutes, onHide }) => {
  // Notes now live in gig_notes; fall back to the legacy description field until re-saved
  const notesOf = (g) => g?.gig_notes ?? g?.description ?? "";
  const [journeyInfo, setJourneyInfo] = useState(null);
  const [loadingJourney, setLoadingJourney] = useState(false);
  const [transportMode, setTransportMode] = useState("driving");
  const [applyBuffer, setApplyBuffer] = useState(true);  // synced from /user/travel-settings on mount
  const [shareEnabled, setShareEnabled] = useState(gig?.share_enabled || false);
  const [activeContact, setActiveContact] = useState(null);
  const [shareToken, setShareToken] = useState(gig?.share_token || null);
  const [shareKind, setShareKind] = useState(null); // "family" | "dep" | null
  const [shareToggling, setShareToggling] = useState(false);
  const [editingField, setEditingField] = useState(null); // "call_time", "dress_code", or "gig_notes"
  const [isSaving, setIsSaving] = useState(false);
  const [dresscodeOpen, setDresscodeOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [withOpen, setWithOpen] = useState(false);
  const [finishingTrip, setFinishingTrip] = useState(false);
  // Multi-day next action state
  const [nextAction, setNextAction] = useState(null);
  const [nextActionLeaveByTime, setNextActionLeaveByTime] = useState(null);
  // True when nextAction/nextActionLeaveByTime came from the gig's own
  // Schedule rather than a computed travel phase — carries its own fixed
  // time, so the live directions fetch below skips it entirely.
  const [nextActionFromSchedule, setNextActionFromSchedule] = useState(false);
  const [loadingNextActionTime, setLoadingNextActionTime] = useState(false);
  // Why a live leave-by couldn't be computed (e.g. no home address) — shown
  // in place of the time so the failure isn't silent.
  const [nextActionLeaveByReason, setNextActionLeaveByReason] = useState(null);
  // Which travel_checkpoints key (if any) the CURRENT next action maps to —
  // e.g. "departed_airport". Non-null only for steps you can manually tick
  // off (not for passive "Travelling..." statuses or Schedule-sourced rows).
  // Ticking it advances the brief immediately instead of waiting for the
  // time-based fallback to catch up.
  const [nextActionCheckpoint, setNextActionCheckpoint] = useState(null);
  const [completingCheckpoint, setCompletingCheckpoint] = useState(false);
  // True for the ambient "Rehearsing/Playing at X" status — nothing to route
  // or tick, so the live leave-by fetch below skips it entirely.
  const [nextActionIsActivity, setNextActionIsActivity] = useState(false);
  const [editValues, setEditValues] = useState({
    call_time:   gig?.call_time   || "",
    start_time:  gig?.start_time  || "",
    fee:         gig?.fee != null ? String(gig.fee) : "",
    dress_code:  gig?.dress_code  || "",
    gig_notes: gig?.gig_notes ?? gig?.description ?? "",
  });
  // Touch race: on iOS the input's onBlur fires BEFORE a tap on the ✓
  // button registers as click. The ✓ button sets this flag in mouseDown
  // + preventDefault so the blur handler knows the next event is a save,
  // not a tap-away.
  const skipBlurRef = useRef(false);

  // Check if performance has started (used to show GIG FINISHED button)
  const hasPerformanceStarted = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const performanceDate = gig?.date ? new Date(gig.date + "T00:00:00") : null;

    return performanceDate && today.getTime() >= performanceDate.getTime();
  };

  // Compute next action for multi-day gigs
  const computeNextAction = () => {
    // Reset once up front — only the "on-site activity" branch below sets this
    // back to true, for this same synchronous call, when it's actually reached.
    setNextActionIsActivity(false);

    if (!gig?.is_multiday) {
      setNextAction(null);
      setNextActionLeaveByTime(null);
      setNextActionFromSchedule(false);
      setNextActionCheckpoint(null);
      return;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const travelDate = gig.travel_date ? new Date(gig.travel_date + 'T00:00:00') : null;
    const perfDate = gig.date ? new Date(gig.date + 'T00:00:00') : null;

    if (!travelDate || !perfDate) {
      setNextAction(null);
      setNextActionLeaveByTime(null);
      setNextActionFromSchedule(false);
      setNextActionCheckpoint(null);
      return;
    }

    const departureAirport = gig.departure_airport?.name || "airport";

    // Manually-ticked checkpoints (the "Done" button below) take priority
    // over the time-based guesses. Without this, "Leave for X" stayed on
    // screen — showing an already-elapsed leave-by time — all the way until
    // the literal flight/departure moment, long after the buffer deadline
    // had passed. The time checks now only serve as a fallback for whichever
    // checkpoint hasn't been ticked yet, so the brief still moves on if you
    // forget to tap through.
    const checkpoints = gig.travel_checkpoints || {};

    // Not departed yet (before travel_date, or on travel_date but still
    // ahead of departure_time — or not yet manually ticked): "Leave for
    // [departure_airport]"
    if (!checkpoints.departed_airport && !hasDepartedForAirport(gig)) {
      const action = `Leave for ${departureAirport}`;
      // Leave-by time will be calculated via Directions API in Part D
      // For now, use departure_time - airport_buffer
      let leaveBy = null;
      if (gig.departure_time && gig.airport_buffer) {
        const [h, m] = gig.departure_time.split(":").map(Number);
        const depMinutes = h * 60 + m;
        const leaveMinutes = depMinutes - gig.airport_buffer;
        if (leaveMinutes >= 0) {
          const lh = Math.floor(leaveMinutes / 60);
          const lm = leaveMinutes % 60;
          leaveBy = `${String(lh).padStart(2, "0")}:${String(lm).padStart(2, "0")}`;
        }
      }
      setNextAction(action);
      setNextActionLeaveByTime(leaveBy);
      setNextActionFromSchedule(false);
      setNextActionCheckpoint("departed_airport");
      return;
    }

    // Departed, but before perf_date: "Travelling to venue"
    if (today < perfDate) {
      setNextAction("Travelling to venue");
      setNextActionLeaveByTime(null);
      setNextActionFromSchedule(false);
      setNextActionCheckpoint(null);
      return;
    }

    // On performance date: the day's own Schedule (running order) beats a
    // recomputed "Leave for venue" — once you're on site, a fixed pickup/
    // soundcheck time is more reliable than a fresh drive-from-home estimate.
    // Also reachable via a manual "arrived_venue" tick even while today still
    // equals perfDate — without that, a SAME-DAY return leg (fly out and
    // back in one day) could never surface, since the code below it only
    // used to run once the calendar rolled past perfDate entirely.
    if (!checkpoints.arrived_venue && today.getTime() === perfDate.getTime()) {
      const scheduleItem = nextUpcomingScheduleItem(gig.schedule);
      if (scheduleItem) {
        setNextAction(scheduleItem.label || "Next up");
        setNextActionLeaveByTime(scheduleItem.time);
        setNextActionFromSchedule(true);
        setNextActionCheckpoint(null);
        return;
      }

      setNextAction(`Leave for ${gig.venue_name || "venue"}`);
      // Leave-by will be call_time - 5 mins (or from Directions API)
      let leaveBy = null;
      if (gig.call_time) {
        const [h, m] = gig.call_time.split(":").map(Number);
        const callMinutes = h * 60 + m;
        const leaveMinutes = callMinutes - 5;
        if (leaveMinutes >= 0) {
          const lh = Math.floor(leaveMinutes / 60);
          const lm = leaveMinutes % 60;
          leaveBy = `${String(lh).padStart(2, "0")}:${String(lm).padStart(2, "0")}`;
        }
      }
      setNextActionLeaveByTime(leaveBy);
      setNextActionFromSchedule(false);
      setNextActionCheckpoint("arrived_venue");
      return;
    }

    // You've arrived. Keep following the day's own Schedule exactly as
    // before arrival — arriving used to skip past everything left on it
    // (soundcheck, performance, finish, load out) straight to the return leg.
    const onSiteScheduleItem = nextUpcomingScheduleItem(gig.schedule);
    if (onSiteScheduleItem) {
      setNextAction(onSiteScheduleItem.label || "Next up");
      setNextActionLeaveByTime(onSiteScheduleItem.time);
      setNextActionFromSchedule(true);
      setNextActionCheckpoint(null);
      return;
    }

    // Nothing left on the Schedule: an ambient "you're here, doing the thing"
    // status — nothing to leave for yet — for as long as the gig is still
    // plausibly running. Prefers the gig's own end_time; falls back to
    // call/start time + 4h (the same default duration used elsewhere, e.g.
    // the calendar sync's event length when no end time is set).
    const stillOnSite = (() => {
      const now = new Date();
      if (gig.end_time) {
        const [eh, em] = gig.end_time.split(":").map(Number);
        const endMoment = new Date(perfDate);
        endMoment.setHours(eh, em, 0, 0);
        return now < endMoment;
      }
      const anchorTime = gig.call_time || gig.start_time;
      if (!anchorTime) return false;
      const [ah, am] = anchorTime.split(":").map(Number);
      const endMoment = new Date(perfDate);
      endMoment.setHours(ah, am, 0, 0);
      endMoment.setHours(endMoment.getHours() + 4);
      return now < endMoment;
    })();

    if (stillOnSite) {
      setNextAction(gigActivityLabel(gig));
      setNextActionLeaveByTime(null);
      setNextActionFromSchedule(false);
      setNextActionIsActivity(true);
      setNextActionCheckpoint(null);
      return;
    }

    // After the performance: heading home. Mirrors the outbound leg, but
    // uses return_date/return_time/destination_airport (the venue-side
    // airport you're flying out FROM) — never a live directions fetch here,
    // since you're not at home to compute a drive from (see
    // fetchNextActionLeaveByTime, which only fetches for the outbound and
    // on-performance-day cases and leaves this static estimate alone).
    if (gig.return_date) {
      const returnDate = new Date(gig.return_date + 'T00:00:00');

      if (!checkpoints.departed_return && !hasDepartedForReturn(gig)) {
        // A scheduled "airport transfer" row beats the airport_buffer
        // estimate outright — someone's picking you up at a fixed time,
        // not a calculation.
        const transferItem = nextUpcomingScheduleItem(gig.schedule, isAirportTransferLabel);
        if (transferItem) {
          setNextAction(transferItem.label || "Airport transfer");
          setNextActionLeaveByTime(transferItem.time);
          setNextActionFromSchedule(true);
          setNextActionCheckpoint(null);
          return;
        }

        const returnAirport = gig.destination_airport?.name || "airport";
        let leaveBy = null;
        if (gig.return_time && gig.airport_buffer) {
          const [h, m] = gig.return_time.split(":").map(Number);
          const depMinutes = h * 60 + m;
          const leaveMinutes = depMinutes - gig.airport_buffer;
          if (leaveMinutes >= 0) {
            const lh = Math.floor(leaveMinutes / 60);
            const lm = leaveMinutes % 60;
            leaveBy = `${String(lh).padStart(2, "0")}:${String(lm).padStart(2, "0")}`;
          }
        }
        setNextAction(`Leave for ${returnAirport}`);
        setNextActionLeaveByTime(leaveBy);
        setNextActionFromSchedule(false);
        setNextActionCheckpoint("departed_return");
        return;
      }

      if (today <= returnDate) {
        setNextAction("Travelling home");
        setNextActionLeaveByTime(null);
        setNextActionFromSchedule(false);
        setNextActionCheckpoint(null);
        return;
      }
    }

    // After the return leg (or no return leg was ever set): trip complete
    setNextAction(null);
    setNextActionLeaveByTime(null);
    setNextActionFromSchedule(false);
    setNextActionCheckpoint(null);
  };

  // Tick a travel checkpoint (see Gig.travel_checkpoints on the backend) and
  // advance the brief immediately, rather than waiting for the time-based
  // fallback to eventually agree. Mirrors handleSaveEdit/toggleTripDone:
  // persist, mutate the shared gig object in place, then re-derive the UI.
  const handleCompleteCheckpoint = async (checkpoint) => {
    if (!checkpoint) return;
    setCompletingCheckpoint(true);
    try {
      const merged = { ...(gig.travel_checkpoints || {}), [checkpoint]: new Date().toISOString() };
      await apiClient.put(`/gigs/${gig.gig_id}`, { travel_checkpoints: merged });
      Object.assign(gig, { travel_checkpoints: merged });
      computeNextAction();
    } catch (error) {
      toast.error("Failed to update");
      console.error(error);
    } finally {
      setCompletingCheckpoint(false);
    }
  };

  // Fetch live leave-by time from Directions API for next action.
  // Sequence-guarded like fetchJourneyTime: only the newest request may write
  // state, so a slow transit response can't overwrite a later driving one.
  const nextActionReqSeq = useRef(0);
  const fetchNextActionLeaveByTime = async () => {
    if (!gig?.is_multiday || !nextAction) return;
    // Schedule-sourced actions already carry their own fixed time (see
    // computeNextAction) — there's no route to recompute.
    if (nextActionFromSchedule) return;
    // Transfer means someone else is driving — there's nothing to route,
    // so keep the static buffer-based estimate computeNextAction already
    // set instead of fetching a live (self-navigation) directions time.
    if (transportMode === 'transfer') return;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const travelDate = gig.travel_date ? new Date(gig.travel_date + 'T00:00:00') : null;
    const perfDate = gig.date ? new Date(gig.date + 'T00:00:00') : null;

    if (!travelDate || !perfDate) return;

    // After the performance: heading home. Same static-only treatment as
    // computeNextAction's return-leg branch — no live fetch, since you're
    // not at home to route a drive from.
    if (today > perfDate) return;

    const seq = ++nextActionReqSeq.current;
    setLoadingNextActionTime(true);
    setNextActionLeaveByReason(null);
    const applyResponse = (response) => {
      if (seq !== nextActionReqSeq.current) return; // superseded by a newer toggle
      if (response.data?.success && response.data?.leave_by_time) {
        setNextActionLeaveByTime(response.data.leave_by_time);
      } else if (response.data?.error) {
        setNextActionLeaveByReason(response.data.error);
      }
    };
    try {
      // Ambient "Rehearsing/Playing at X" status — nothing to route while
      // you're on site doing the thing. Without this, the "current location
      // → venue" branch below would still match (today===perfDate and
      // call_time is set) and overwrite the null leave-by with a stale
      // "leave for venue" recompute.
      if (nextActionIsActivity) return;

      // Not departed yet: home → departure_airport. Buffer is optional — the
      // backend falls back to the user's default buffer when it's omitted.
      if (!hasDepartedForAirport(gig) && gig.departure_time) {
        const payload = {
          gig_id: gig.gig_id,
          transport_mode: transportMode,
          apply_buffer: applyBuffer,
          destination_type: "airport",
          destination_airport: gig.departure_airport?.name || "airport",
          reference_time: gig.departure_time,
        };
        if (gig.airport_buffer) payload.buffer_minutes = gig.airport_buffer;
        applyResponse(await apiClient.post("/travel/journey-time", payload));
        return;
      }

      // On performance date: current location → venue
      if (today.getTime() === perfDate.getTime() && gig.call_time) {
        applyResponse(await apiClient.post("/travel/journey-time", {
          gig_id: gig.gig_id,
          transport_mode: transportMode,
          apply_buffer: applyBuffer,
          destination_type: "venue",
          reference_time: gig.call_time,
          buffer_minutes: 5,
        }));
        return;
      }
    } catch (error) {
      console.error("Failed to fetch next action leave-by time:", error);
    } finally {
      if (seq === nextActionReqSeq.current) setLoadingNextActionTime(false);
    }
  };

  useEffect(() => {
    // Load the user's saved buffer-toggle preference once on mount, and — if
    // the gig doesn't specify its own transport — default the mode toggle to
    // the user's saved transport preference instead of always "driving".
    (async () => {
      try {
        const res = await apiClient.get("/user/travel-settings");
        if (res.data?.apply_buffer_in_dashboard != null) {
          setApplyBuffer(!!res.data.apply_buffer_in_dashboard);
        }
        if (!gig?.transport_type && res.data?.default_transport_mode === "transit") {
          setTransportMode("transit");
        }
      } catch {}
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // The gig's own Transport field wins over the settings default: a gig
  // marked "Public Transport" should open showing the train journey, not
  // the hardcoded driving estimate. "pickup" means someone else drives —
  // maps to the transfer mode on multi-day briefs (which support it);
  // domestic briefs treat it as driving (closest leave-by estimate).
  useEffect(() => {
    if (gig?.transport_type === "public_transport") setTransportMode("transit");
    else if (gig?.transport_type === "driving") setTransportMode("driving");
    else if (gig?.transport_type === "pickup") setTransportMode(gig?.is_multiday ? "transfer" : "driving");
  }, [gig?.gig_id, gig?.transport_type, gig?.is_multiday]);

  // Compute next action whenever gig changes
  useEffect(() => {
    computeNextAction();
  }, [gig?.gig_id, gig?.is_multiday, gig?.travel_date, gig?.date, gig?.departure_time, gig?.airport_buffer, gig?.call_time, gig?.venue_name, gig?.return_date, gig?.return_time, gig?.destination_airport]);

  // Fetch live leave-by time for next action when it changes or transport mode changes
  useEffect(() => {
    if (nextAction) {
      fetchNextActionLeaveByTime();
    }
  }, [nextAction, transportMode, applyBuffer, gig?.gig_id]);

  useEffect(() => {
    if (gig?.call_time) fetchJourneyTime();
  }, [gig?.gig_id, transportMode, applyBuffer]);

  // Guards against out-of-order responses: toggling transit→driving fires two
  // requests, and the transit one is much slower (station-timetable lookup is
  // two Google calls), so without this it lands last and overwrites the
  // driving result — leaving a train row on screen with the car toggle
  // selected. Only the newest request may write state; a failed newest
  // request clears the display rather than leaving the previous mode's
  // result up as if it were current.
  const journeyReqSeq = useRef(0);
  const fetchJourneyTime = async () => {
    if (!gig?.call_time) return;
    const seq = ++journeyReqSeq.current;
    setLoadingJourney(true);
    try {
      const response = await apiClient.post("/travel/journey-time", {
        gig_id: gig.gig_id,
        transport_mode: transportMode,
        apply_buffer: applyBuffer,
      });
      if (seq !== journeyReqSeq.current) return; // superseded by a newer toggle
      setJourneyInfo(response.data);
    } catch (error) {
      console.error("Failed to fetch journey time:", error);
      if (seq === journeyReqSeq.current) {
        setJourneyInfo({ success: false, error: "Couldn't fetch travel time — try again" });
      }
    } finally {
      if (seq === journeyReqSeq.current) setLoadingJourney(false);
    }
  };

  const toggleBuffer = async () => {
    const next = !applyBuffer;
    setApplyBuffer(next);
    // Persist the preference (fire-and-forget — UI updates immediately)
    try {
      await apiClient.put("/user/travel-settings", { apply_buffer_in_dashboard: next });
    } catch (err) {
      console.error("Failed to save buffer preference:", err);
    }
  };

  // Responsive dispatch: desktop variant for ≥900px viewport
  const isDesktop = useIsDesktop(900);
  if (isDesktop) {
    return <GigBriefDesktop gig={gig} followingGig={followingGig} collaborators={collaborators} onHide={onHide} />;
  }

  if (!gig) return null;

  // For multi-day gigs, display travel_date as headline; for domestic, use gig.date
  const displayDateStr = gig.is_multiday && gig.travel_date ? gig.travel_date : gig.date;
  const gigDate = displayDateStr ? parseISO(displayDateStr) : null;
  // Guard: bail out gracefully if gig has no valid date so format() doesn't
  // throw "Invalid time value" and blank the dashboard.
  if (!gigDate || isNaN(gigDate.getTime())) return null;

  const gigDayStr = gigLocalDate(displayDateStr);
  const todayStr = new Date().toLocaleDateString('en-CA');
  const daysUntil = Math.round(
    (new Date(gigDayStr + 'T00:00:00') - new Date(todayStr + 'T00:00:00')) / 86400000
  );

  // Once we're past this gig's own performance date — i.e. on the return
  // leg — the urgency badge switches to the NEXT actual gig instead of
  // showing a negative day count for a trip that's already happened. The
  // brief body below still shows this gig's travel-day details (Next
  // Action, etc.) unchanged.
  const perfDayStr = gig.date ? gigLocalDate(gig.date) : null;
  const pastPerformance = gig.is_multiday && perfDayStr && todayStr > perfDayStr;
  let badgeDate = gigDate;
  let badgeDaysUntil = daysUntil;
  if (pastPerformance) {
    const followingDateStr = followingGig?.date ? gigLocalDate(followingGig.date) : null;
    const followingDate = followingDateStr ? parseISO(followingDateStr) : null;
    if (followingDate && !isNaN(followingDate.getTime())) {
      badgeDate = followingDate;
      badgeDaysUntil = Math.round(
        (new Date(followingDateStr + 'T00:00:00') - new Date(todayStr + 'T00:00:00')) / 86400000
      );
    } else {
      badgeDate = null; // no following gig — suppress rather than show a negative count
    }
  }

  // Directions label reflects the routing destination (airport / venue / default)
  const navAirport = resolveNavAirport();
  const directionsLabel = navAirport
    ? 'Get to airport'
    : (gig.is_multiday && nextAction ? 'Get to venue' : 'Directions');

  const getUrgencyBadge = () => {
    if (!badgeDate) return null;
    if (isToday(badgeDate)) {
      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            backgroundColor: briefPal.red,
            color: "white",
            padding: "4px 9px 4px 8px",
            borderRadius: briefRad.pill,
            fontFamily: briefFont.mono,
            fontWeight: 600,
            fontSize: "10px",
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            whiteSpace: "nowrap"
          }}
        >
          <div
            style={{
              width: "6px",
              height: "6px",
              borderRadius: briefRad.pill,
              backgroundColor: "white",
              opacity: 0.8,
              animation: "pulse 1.4s ease-in-out infinite"
            }}
          />
          Today
        </div>
      );
    } else if (isTomorrow(badgeDate)) {
      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            backgroundColor: briefPal.amber,
            color: briefPal.onAmber,
            padding: "4px 9px 4px 8px",
            borderRadius: briefRad.pill,
            fontFamily: briefFont.mono,
            fontWeight: 600,
            fontSize: "10px",
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            whiteSpace: "nowrap"
          }}
        >
          <div
            style={{
              width: "6px",
              height: "6px",
              borderRadius: briefRad.pill,
              backgroundColor: briefPal.onAmber,
              opacity: 0.8
            }}
          />
          Tomorrow
        </div>
      );
    } else if (badgeDaysUntil <= 7 && badgeDaysUntil > 0) {
      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            backgroundColor: briefPal.orange,
            color: briefPal.onOrange,
            padding: "4px 9px 4px 8px",
            borderRadius: briefRad.pill,
            fontFamily: briefFont.mono,
            fontWeight: 600,
            fontSize: "10px",
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            whiteSpace: "nowrap"
          }}
        >
          <div
            style={{
              width: "6px",
              height: "6px",
              borderRadius: briefRad.pill,
              backgroundColor: briefPal.onOrange,
              opacity: 0.8
            }}
          />
          {badgeDaysUntil} days
        </div>
      );
    }
    return null;
  };

  // Phase-aware nav target: before travel → departure airport, after the gig →
  // the venue-side airport you fly OUT of for the return leg, otherwise null
  // (route to venue). Reuses the same date logic that drives nextAction so
  // the Directions button matches the brief's phase.
  //
  // The return leg departs from destination_airport (where the outbound
  // flight landed, near the venue — e.g. Marseille), NOT return_airport
  // (where the return flight lands, back home — e.g. Heathrow). Sent people
  // to the wrong airport entirely before this fix.
  function resolveNavAirport() {
    if (!gig?.is_multiday) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const travelDate = gig.travel_date ? new Date(gig.travel_date + 'T00:00:00') : null;
    const perfDate = gig.date ? new Date(gig.date + 'T00:00:00') : null;
    if (!travelDate || !perfDate) return null;
    if (!hasDepartedForAirport(gig)) return gig.departure_airport || null;
    // Once the return flight has actually departed, there's nothing left to
    // navigate to — without this check, the button kept pointing back at
    // the venue-side airport for the rest of the return day even after
    // landing back home.
    if (today > perfDate && !hasDepartedForReturn(gig)) return gig.destination_airport || gig.departure_airport || null;
    return null; // travel window / gig day / post-return → venue
  }

  // Citymapper's /directions link treats endcoord as the destination proper;
  // endname/endaddress are display labels only. Without endcoord an
  // address-only link now lands on Citymapper's generic landing page with
  // nothing pre-filled — it used to geocode the address as a courtesy, but
  // that behaviour changed on their side (July 2026). So Citymapper is the
  // one nav provider that uses stored venue coordinates; Google/Waze stay
  // address-only since they geocode text reliably and text can't go stale
  // the way a cached coordinate can. Venue coords are kept in sync with the
  // canonical address by the backend's geocode-on-save, which is what makes
  // this acceptable where it once wasn't.
  const citymapperUrl = ({ lat, lng, name, address }) => {
    const params = [];
    if (lat != null && lng != null) params.push(`endcoord=${lat},${lng}`);
    if (name) params.push(`endname=${encodeURIComponent(name)}`);
    if (address) params.push(`endaddress=${encodeURIComponent(address)}`);
    return `https://citymapper.com/directions?${params.join("&")}`;
  };

  const openNavigation = (provider) => {
    // Airport phase: route by airport name (+ place_id where Maps supports it).
    const airport = resolveNavAirport();
    if (airport?.name) {
      const enc = encodeURIComponent(airport.name);
      const airportUrls = {
        google: `https://www.google.com/maps/dir/?api=1&destination=${enc}${airport.place_id ? `&destination_place_id=${airport.place_id}` : ''}`,
        waze: `https://waze.com/ul?q=${enc}&navigate=yes`,
        citymapper: citymapperUrl({ name: airport.name, address: airport.address || airport.name }),
      };
      openExternalLink(airportUrls[provider]);
      return;
    }
    // Not in the airport phase: route to the venue (shared with the Venue
    // card's own Directions button, so there's one venue-routing code path).
    openVenueNavigation(provider);
  };

  // Always route to the VENUE regardless of trip phase, by address/name text
  // for Google/Waze — never cached venue_lat/venue_lng there. A stale or
  // wrong cached coordinate (e.g. from an earlier bad geocode) would silently
  // win over a correct address every time, which is exactly how this sent
  // people to the wrong place; address-only search has no such cache to go
  // stale. Citymapper is the exception — see citymapperUrl above.
  const openVenueNavigation = (provider) => {
    const venueAddress = gig.venue_address
      ? `${gig.venue_name}, ${gig.venue_address}`
      : `${gig.venue_name}, ${gig.city}`;
    if (!venueAddress) return;
    const enc = encodeURIComponent(venueAddress);
    const urls = {
      google: `https://www.google.com/maps/search/?api=1&query=${enc}`,
      waze: `https://waze.com/ul?q=${enc}&navigate=yes`,
      citymapper: citymapperUrl({ lat: gig.venue_lat, lng: gig.venue_lng, name: gig.venue_name, address: venueAddress }),
    };
    openExternalLink(urls[provider]);
  };

  // Route to the HOTEL by its address (or name as a fallback). Independent of
  // trip phase — the Hotel card always points at the accommodation.
  const openHotelNavigation = (provider) => {
    const hotelAddress = gig.accommodation?.address || gig.accommodation?.name;
    if (!hotelAddress) return;
    const enc = encodeURIComponent(hotelAddress);
    const urls = {
      google: `https://www.google.com/maps/search/?api=1&query=${enc}`,
      waze: `https://waze.com/ul?q=${enc}&navigate=yes`,
      citymapper: citymapperUrl({ name: gig.accommodation?.name, address: hotelAddress }),
    };
    openExternalLink(urls[provider]);
  };


  const gigCollaborators = gigContactsFor(gig, collaborators, notesOf(gig));

  // Single-line readability: show Notes / Gig contacts inline when they fit on
  // one line, and only fall back to a collapsible dropdown when the note is long
  // or there are too many contacts to read at a glance. (Dress code is always
  // short, so it's always inline — no dropdown.)
  const notesText = notesOf(gig) || "";
  const notesFitInline = notesText.length <= 40 && !notesText.includes("\n");
  const dressText = gig.dress_code || "";
  const dressFitInline = dressText.length <= 40 && !dressText.includes("\n");
  const contactsFitInline = gigCollaborators.length <= 2;

  const handleShareToggle = async () => {
    setShareToggling(true);
    try {
      const res = await apiClient.put(`/gigs/${gig.gig_id}/share`);
      setShareEnabled(res.data.share_enabled);
      setShareToken(res.data.share_token);
      toast.success(res.data.share_enabled ? "Sharing enabled" : "Sharing disabled");
    } catch {
      toast.error("Failed to toggle sharing");
    } finally {
      setShareToggling(false);
    }
  };

  // fee is numeric; the other editable fields are strings. Empty -> null.
  const coerceForSave = (field, raw) => {
    if (field === "fee") {
      if (raw === "" || raw == null) return null;
      const n = parseFloat(raw);
      return Number.isFinite(n) ? n : null;
    }
    return raw === "" ? null : raw;
  };

  const isUnchanged = (field, raw) => {
    const current = gig?.[field];
    if ((current == null || current === "") && (raw == null || raw === "")) return true;
    return String(current ?? "") === String(raw ?? "");
  };

  const handleSaveEdit = async (field) => {
    const raw = editValues[field];
    if (isUnchanged(field, raw)) {
      setEditingField(null);
      return;
    }
    setIsSaving(true);
    try {
      const value = coerceForSave(field, raw);
      const updates = { [field]: value };
      if (field === "gig_notes") updates.description = null; // legacy field, now unused
      await apiClient.put(`/gigs/${gig.gig_id}`, updates);
      Object.assign(gig, updates);
      toast.success("Updated");
      setEditingField(null);
    } catch (error) {
      toast.error("Failed to save changes");
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  // Multi-day "Trip done": mark the trip complete so the next-action tracker
  // stops surfacing prompts. Mirrors handleSaveEdit (persist, then mutate in place).
  const toggleTripDone = async () => {
    const next = !gig.gig_finished;
    setFinishingTrip(true);
    try {
      await apiClient.put(`/gigs/${gig.gig_id}`, { gig_finished: next });
      Object.assign(gig, { gig_finished: next });
      toast.success(next ? "Trip marked done" : "Trip reopened");
    } catch (error) {
      toast.error("Failed to update");
      console.error(error);
    } finally {
      setFinishingTrip(false);
    }
  };

  const handleCancelEdit = () => {
    setEditValues({
      call_time:   gig?.call_time   || "",
      start_time:  gig?.start_time  || "",
      fee:         gig?.fee != null ? String(gig.fee) : "",
      dress_code:  gig?.dress_code  || "",
      gig_notes: gig?.gig_notes ?? gig?.description ?? "",
    });
    setEditingField(null);
  };

  // Blur = commit. iOS quirks driving this:
  //   - <Input type="time"> on iOS PWA: tapping the native picker's Done
  //     button fires blur but NO keydown "Enter", so a blur-cancel handler
  //     silently wipes the new value.
  //   - iOS keyboard's return / tick on text inputs also doesn't reliably
  //     fire keydown "Enter" inside a PWA.
  // So we treat blur as the universal commit signal. The orange ✓ button
  // still sets skipBlurRef in its pointer/touch/mouse-down handlers, so
  // when it's tapped the blur handler short-circuits and the explicit
  // click → handleSaveEdit runs (no double-save).
  const inputBlurCommit = (field) => ({
    onBlur: () => {
      if (skipBlurRef.current) {
        skipBlurRef.current = false;
        return;
      }
      handleSaveEdit(field);
    },
  });
  // <Input> variant: blur-commits, plus keyboard Enter (desktop / any iOS
  // keyboard that does fire it) also commits. preventDefault stops the
  // ghost form-submit that some iOS keyboards trigger.
  const inputEditHandlers = (field) => ({
    ...inputBlurCommit(field),
    onKeyDown: (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        skipBlurRef.current = true;
        handleSaveEdit(field);
      } else if (e.key === "Escape") {
        e.preventDefault();
        skipBlurRef.current = true;
        handleCancelEdit();
      }
    },
  });
  // ✓ button wiring. iOS Safari order on tap: touchstart → input blur →
  // mousedown → click. If we only set skipBlurRef in onMouseDown, the blur
  // handler runs first with skipBlurRef=false, cancels the edit, and the
  // button gets unmounted before its click fires. So we set the flag in
  // onTouchStart and onPointerDown too — whichever fires first wins.
  // preventDefault on the down events keeps the input focused on desktop.
  const armSkipBlur = (e) => { skipBlurRef.current = true; if (e.cancelable) e.preventDefault(); };
  const saveBtnHandlers = (field) => ({
    onTouchStart: armSkipBlur,
    onPointerDown: armSkipBlur,
    onMouseDown: armSkipBlur,
    onClick: () => handleSaveEdit(field),
    disabled: isSaving,
  });
  // Cancel (X) button. Same skipBlurRef arming as the save tick so the
  // input's blur handler doesn't fire its commit before this click runs.
  const cancelBtnHandlers = {
    onTouchStart: armSkipBlur,
    onPointerDown: armSkipBlur,
    onMouseDown: armSkipBlur,
    onClick: () => handleCancelEdit(),
    disabled: isSaving,
  };
  const CancelXButton = () => (
    <button
      {...cancelBtnHandlers}
      aria-label="Cancel"
      style={{
        padding: "6px 10px",
        borderRadius: briefRad.sm,
        backgroundColor: "transparent",
        border: `1px solid ${briefPal.innerBorder}`,
        color: briefPal.textMuted,
        cursor: "pointer",
        flexShrink: 0,
      }}
    >
      <X className="h-3 w-3" />
    </button>
  );
  // Re-usable ✓ button visual.
  const SaveTickButton = (field) => (
    <button
      {...saveBtnHandlers(field)}
      aria-label="Save"
      style={{
        padding: "6px 10px",
        borderRadius: briefRad.sm,
        backgroundColor: briefPal.orange,
        border: "none",
        cursor: "pointer",
        flexShrink: 0,
      }}
    >
      {isSaving ? (
        <Loader2 className="h-3 w-3 animate-spin" style={{ color: "white" }} />
      ) : (
        <Check className="h-3 w-3" style={{ color: "white" }} />
      )}
    </button>
  );

  const apiBase = API.startsWith('http') ? API : window.location.origin + API;
  const briefShareUrl = shareToken ? `${apiBase}/share/gig/${shareToken}/og` : null;
  // The shared link always shows live gig data, so edits made here reach it automatically.
  return (
    <div style={{ width: "100%", maxWidth: 384 }}>
      <div
        style={{
          backgroundColor: briefPal.cardBg,
          border: briefBorder.card,
          boxShadow: briefShadow.card,
          borderRadius: briefRad.xl,
          overflow: "hidden",
          fontFamily: briefFont.body,
          color: briefPal.text
        }}
        data-testid="gig-brief"
      >
        {/* 1. Header: NEXT GIG + urgency badge + close button */}
        <div
          style={{
            padding: "16px",
            borderBottom: briefBorder.hairline,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "12px"
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px", minWidth: 0, flex: 1 }}>
            <div
              style={{
                fontFamily: briefFont.display,
                fontWeight: 600,
                fontSize: "18px",
                letterSpacing: "-0.02em",
                color: briefPal.text,
                whiteSpace: "nowrap"
              }}
            >
              Next gig
            </div>
            {getUrgencyBadge()}
            {/* When the badge has switched to the next gig (this one's on its
                return leg), name it — otherwise "5 days" reads as if it's
                about the gig shown below. */}
            {pastPerformance && badgeDate && followingGig?.artist_name && (
              <span
                style={{
                  fontFamily: briefFont.body,
                  fontSize: "12px",
                  color: briefPal.textMuted,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  minWidth: 0,
                }}
              >
                {followingGig.artist_name}
              </span>
            )}
          </div>
          {onHide && (
            <button
              type="button"
              onClick={onHide}
              aria-label="Hide widget"
              title="Hide widget"
              data-testid="gig-brief-hide"
              style={{
                width: 28,
                height: 28,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                background: "transparent",
                color: briefPal.textMuted,
                border: "none",
                borderRadius: briefRad.sm,
                cursor: "pointer",
                flexShrink: 0,
                padding: 0
              }}
            >
              <X style={{ width: 14, height: 14 }} />
            </button>
          )}
        </div>

        {/* 2. Artist name */}
        <div
          style={{
            padding: "12px 16px",
            textAlign: "center",
            borderBottom: briefBorder.hairline
          }}
        >
          <h3
            style={{
              fontFamily: briefFont.display,
              fontWeight: 600,
              fontSize: "24px",
              letterSpacing: "-0.025em",
              lineHeight: 1,
              color: briefPal.text,
              overflowWrap: "break-word",
              margin: 0
            }}
          >
            {gig.artist_name}
          </h3>
        </div>

        {/* Content sections container */}
        <div style={{ padding: "16px" }}>

          {/* Trip date blocks — multi-day only (no empty gap for single gigs) */}
          {gig.is_multiday && (
            <div style={{ marginBottom: "16px" }}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: gig.return_date ? "repeat(3, 1fr)" : "repeat(2, 1fr)",
                  gap: "12px",
                }}
              >
                <div
                  style={{
                    padding: "12px",
                    backgroundColor: briefPal.innerBg,
                    borderRadius: briefRad.md,
                    textAlign: "center",
                    fontSize: "12px",
                    fontWeight: 600,
                    color: briefPal.text,
                    border: `2px solid ${gig.travel_date && gigLocalDate(gig.travel_date) === todayStr ? TRIP_LEG_TODAY_BORDER : 'transparent'}`,
                  }}
                >
                  <div style={{ fontSize: "16px", marginBottom: "4px" }}>✈️</div>
                  <div>{gig.travel_date ? format(parseISO(gig.travel_date), 'EEE MMM d') : '—'}</div>
                </div>
                <div
                  style={{
                    padding: "12px",
                    backgroundColor: briefPal.innerBg,
                    borderRadius: briefRad.md,
                    textAlign: "center",
                    fontSize: "12px",
                    fontWeight: 600,
                    color: briefPal.text,
                    border: `2px solid ${gig.date && gigLocalDate(gig.date) === todayStr ? TRIP_LEG_TODAY_BORDER : 'transparent'}`,
                  }}
                >
                  <div style={{ fontSize: "16px", marginBottom: "4px" }}>🎵</div>
                  <div>{gig.date ? format(parseISO(gig.date), 'EEE MMM d') : '—'}</div>
                </div>
                {gig.return_date && (
                  <div
                    style={{
                      padding: "12px",
                      backgroundColor: briefPal.innerBg,
                      borderRadius: briefRad.md,
                      textAlign: "center",
                      fontSize: "12px",
                      fontWeight: 600,
                      color: briefPal.text,
                      border: `2px solid ${gigLocalDate(gig.return_date) === todayStr ? TRIP_LEG_TODAY_BORDER : 'transparent'}`,
                    }}
                  >
                    <div style={{ fontSize: "16px", marginBottom: "4px" }}>✈️</div>
                    <div>{format(parseISO(gig.return_date), 'EEE MMM d')}</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Next Action Section — only for multi-day gigs with a valid next action */}
          {gig.is_multiday && nextAction && !gig.gig_finished && (
            <div style={{ marginBottom: "16px" }}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                }}
              >
                <div>
                  <div
                    style={{
                      fontFamily: briefFont.mono,
                      fontSize: "9px",
                      fontWeight: 600,
                      letterSpacing: "0.12em",
                      textTransform: "uppercase",
                      color: briefPal.textMuted,
                      marginBottom: "6px"
                    }}
                  >
                    Next action
                  </div>
                  <div style={{ color: briefPal.text, fontWeight: 500, fontSize: "14px" }}>
                    {nextAction}
                  </div>
                </div>

                {/* Controls: Transport | Leave by | Go. Transport toggle only
                    makes sense when there's a route to actually calculate —
                    hidden for Schedule-sourced actions (soundcheck, set
                    times) and for Transfer (someone else is driving), since
                    neither has a travel time to toggle between. */}
                {(nextActionLeaveByTime || nextActionLeaveByReason || loadingNextActionTime) && (() => {
                  const showTransportToggle = !nextActionFromSchedule && transportMode !== 'transfer';
                  return (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: "12px",
                    }}
                  >
                      {/* Transport toggle */}
                      {showTransportToggle && (
                      <div
                        style={{
                          display: "inline-flex",
                          gap: "2px",
                          backgroundColor: briefPal.segBg,
                          borderRadius: briefRad.md,
                          padding: "2px"
                        }}
                      >
                        <button
                          onClick={() => setTransportMode('driving')}
                          style={{
                            width: "36px",
                            height: "32px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            borderRadius: briefRad.sm,
                            backgroundColor: transportMode === 'driving' ? briefPal.segActive : 'transparent',
                            border: "none",
                            cursor: "pointer",
                            transition: "all 150ms ease"
                          }}
                        >
                          <Car
                            className="h-4 w-4"
                            style={{ color: transportMode === 'driving' ? briefPal.text : briefPal.textMuted }}
                          />
                        </button>
                        <button
                          onClick={() => setTransportMode('transit')}
                          style={{
                            width: "36px",
                            height: "32px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            borderRadius: briefRad.sm,
                            backgroundColor: transportMode === 'transit' ? briefPal.segActive : 'transparent',
                            border: "none",
                            cursor: "pointer",
                            transition: "all 150ms ease"
                          }}
                        >
                          <Train
                            className="h-4 w-4"
                            style={{ color: transportMode === 'transit' ? briefPal.text : briefPal.textMuted }}
                          />
                        </button>
                        <button
                          onClick={() => setTransportMode('transfer')}
                          title="Transfer (someone else is driving)"
                          style={{
                            width: "36px",
                            height: "32px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            borderRadius: briefRad.sm,
                            backgroundColor: transportMode === 'transfer' ? briefPal.segActive : 'transparent',
                            border: "none",
                            cursor: "pointer",
                            transition: "all 150ms ease"
                          }}
                        >
                          <Users
                            className="h-4 w-4"
                            style={{ color: transportMode === 'transfer' ? briefPal.text : briefPal.textMuted }}
                          />
                        </button>
                      </div>
                      )}

                      {/* Leave by time */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          flexShrink: 0,
                          marginRight: showTransportToggle ? 0 : "auto",
                        }}
                      >
                        {loadingNextActionTime ? (
                          <Loader2 className="h-4 w-4 animate-spin" style={{ color: briefPal.amber }} />
                        ) : nextActionLeaveByTime ? (
                          <div
                            style={{
                              fontFamily: briefFont.body,
                              fontWeight: 700,
                              fontSize: "16px",
                              color: briefPal.amber,
                              whiteSpace: "nowrap"
                            }}
                          >
                            {nextActionLeaveByTime}
                          </div>
                        ) : (
                          <div
                            style={{
                              fontSize: "11px",
                              color: briefPal.textMuted,
                              textAlign: "right",
                              lineHeight: 1.35
                            }}
                          >
                            {nextActionLeaveByReason}
                          </div>
                        )}
                      </div>

                      {/* Go button */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            style={{
                              border: "none",
                              borderRadius: briefRad.md,
                              padding: "10px 14px",
                              backgroundColor: briefPal.orange,
                              color: briefPal.onOrange,
                              fontFamily: briefFont.body,
                              fontSize: "13px",
                              fontWeight: 600,
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              whiteSpace: "nowrap",
                              flexShrink: 0
                            }}
                          >
                            <Navigation className="h-4 w-4" />
                            Go
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openNavigation('google')}>Google Maps</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openNavigation('waze')}>Waze</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openNavigation('citymapper')}>Citymapper</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  );
                })()}

                {/* Mark this step done — advances the brief immediately
                    instead of waiting for the time-based fallback to
                    eventually agree. Only shown for steps that CAN be
                    manually ticked (see nextActionCheckpoint in
                    computeNextAction) — not for passive "Travelling..."
                    statuses or Schedule-sourced rows, which have nothing to
                    tick. */}
                {nextActionCheckpoint && (
                  <button
                    type="button"
                    onClick={() => handleCompleteCheckpoint(nextActionCheckpoint)}
                    disabled={completingCheckpoint}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "6px",
                      width: "100%",
                      padding: "9px 12px",
                      borderRadius: briefRad.md,
                      border: `1px solid ${briefPal.innerBorder}`,
                      backgroundColor: "transparent",
                      color: briefPal.textMuted,
                      fontFamily: briefFont.body,
                      fontSize: "12.5px",
                      fontWeight: 600,
                      cursor: completingCheckpoint ? "default" : "pointer",
                    }}
                    data-testid="next-action-done-btn"
                  >
                    {completingCheckpoint ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Check className="h-3.5 w-3.5" />
                    )}
                    Done — {nextAction}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Date / Call time / Gig starts */}
          <div style={{ marginBottom: "16px", borderTop: briefBorder.hairline, paddingTop: "12px" }}>
            <div style={{ display: "grid", gridTemplateColumns: gig.is_multiday ? "1fr 1fr" : "1fr 1fr 1fr", gap: "16px" }}>
              {/* Date (domestic only — multi-day shows its own date blocks above) */}
              {!gig.is_multiday && (
                <div>
                  <div style={{ fontSize: "12px", color: briefPal.textDim, marginBottom: "6px" }}>Date</div>
                  <div style={{ fontSize: "18px", fontWeight: 600, color: briefPal.text, padding: "4px 0" }}>
                    {format(gigDate, "MMMM do")}
                  </div>
                </div>
              )}

              {/* Call time */}
              <div>
                <div style={{ fontSize: "12px", color: briefPal.textDim, marginBottom: "6px" }}>Call time</div>
                {editingField === 'call_time' ? (
                  <div style={{ display: "flex", gap: "6px" }}>
                    <Input
                      type="time"
                      value={editValues.call_time}
                      onChange={(e) => setEditValues({...editValues, call_time: e.target.value})}
                      autoFocus
                      {...inputEditHandlers('call_time')}
                      style={{
                        fontSize: "13px",
                        flex: "1",
                        backgroundColor: briefPal.innerBg,
                        borderColor: briefPal.innerBorder,
                        color: briefPal.text
                      }}
                    />
                    <CancelXButton />
                    {SaveTickButton('call_time')}
                  </div>
                ) : (
                  <button
                    onClick={() => setEditingField('call_time')}
                    style={{
                      fontSize: "18px",
                      fontWeight: 600,
                      color: gig.call_time ? briefPal.amber : briefPal.textMuted,
                      backgroundColor: "transparent",
                      border: "none",
                      cursor: "pointer",
                      padding: "4px 0",
                      textAlign: "left"
                    }}
                  >
                    {gig.call_time || "—"}
                  </button>
                )}
              </div>

              {/* Gig starts */}
              <div>
                <div style={{ fontSize: "12px", color: briefPal.textDim, marginBottom: "6px" }}>Gig starts</div>
                {editingField === 'start_time' ? (
                  <div style={{ display: "flex", gap: "6px" }}>
                    <Input
                      type="time"
                      value={editValues.start_time}
                      onChange={(e) => setEditValues({...editValues, start_time: e.target.value})}
                      autoFocus
                      {...inputEditHandlers('start_time')}
                      style={{
                        fontSize: "13px",
                        flex: "1",
                        backgroundColor: briefPal.innerBg,
                        borderColor: briefPal.innerBorder,
                        color: briefPal.text
                      }}
                    />
                    <CancelXButton />
                    {SaveTickButton('start_time')}
                  </div>
                ) : (
                  <button
                    onClick={() => setEditingField('start_time')}
                    style={{
                      fontSize: "18px",
                      fontWeight: 600,
                      color: gig.start_time ? briefPal.text : briefPal.textMuted,
                      backgroundColor: "transparent",
                      border: "none",
                      cursor: "pointer",
                      padding: "4px 0",
                      textAlign: "left"
                    }}
                  >
                    {gig.start_time || "—"}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Venue & Accommodation */}
          <div style={{ marginBottom: "16px", borderTop: briefBorder.hairline, paddingTop: "12px" }}>
            <div
              style={{
                fontFamily: briefFont.mono,
                fontSize: "9px",
                fontWeight: 600,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: briefPal.textMuted,
                marginBottom: "10px"
              }}
            >
              Venue {gig.is_multiday ? "& accommodation" : ""}
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: gig.is_multiday ? "1fr 1fr" : "1fr",
                gap: "12px",
                width: "100%",
                minWidth: 0
              }}
            >
              {/* Venue tile */}
              <div
                style={{
                  padding: "12px",
                  backgroundColor: briefPal.innerBg,
                  borderRadius: briefRad.md,
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  minWidth: 0,
                  overflow: "hidden"
                }}
              >
                <div style={{ fontFamily: briefFont.body, fontWeight: 600, fontSize: "13.5px", color: briefPal.text, lineHeight: 1.3, overflowWrap: "break-word", marginBottom: "12px" }}>
                  {gig.venue_name || "—"}
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      style={{
                        border: "none",
                        borderRadius: briefRad.md,
                        padding: "8px 12px",
                        backgroundColor: briefPal.orange,
                        color: briefPal.onOrange,
                        fontFamily: briefFont.body,
                        fontSize: "12px",
                        fontWeight: 600,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "5px",
                        width: "100%",
                        marginTop: "auto"
                      }}
                    >
                      <Navigation className="h-3 w-3" />
                      Directions
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="center">
                    <DropdownMenuItem onClick={() => openVenueNavigation('google')}>Google Maps</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => openVenueNavigation('waze')}>Waze</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => openVenueNavigation('citymapper')}>Citymapper</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {/* Hotel tile (multi-day only) */}
              {gig.is_multiday && (
                <div
                  style={{
                    padding: "12px",
                    backgroundColor: briefPal.innerBg,
                    borderRadius: briefRad.md,
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    minWidth: 0,
                    overflow: "hidden"
                  }}
                >
                  <div style={{ fontSize: "10px", fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase", color: briefPal.textMuted, marginBottom: "6px" }}>
                    Hotel
                  </div>
                  <div style={{ fontFamily: briefFont.body, fontWeight: 600, fontSize: "13.5px", color: briefPal.text, lineHeight: 1.3, overflowWrap: "break-word", marginBottom: "12px" }}>
                    {gig.accommodation?.name || "Hotel pending"}
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        style={{
                          border: "none",
                          borderRadius: briefRad.md,
                          padding: "8px 12px",
                          backgroundColor: briefPal.orange,
                          color: briefPal.onOrange,
                          fontFamily: briefFont.body,
                          fontSize: "12px",
                          fontWeight: 600,
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "5px",
                          width: "100%",
                          marginTop: "auto"
                        }}
                      >
                        <Navigation className="h-3 w-3" />
                        Directions
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="center">
                      <DropdownMenuItem onClick={() => openHotelNavigation('google')}>Google Maps</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openHotelNavigation('waze')}>Waze</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openHotelNavigation('citymapper')}>Citymapper</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              )}
            </div>
          </div>

          {!gig.is_multiday && (
            <>
              {/* 3b. Hairline Divider */}
              <div
                style={{
                  height: "1px",
                  backgroundColor: briefPal.divider,
                  margin: "0 14px"
                }}
              />

              {/* 3c. Leave-by Row */}
              <div
                style={{
                  padding: "14px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between"
                }}
              >
                {/* Transport Toggle — chip on innerBg surface, no outline */}
                <div
                  style={{
                    display: "inline-flex",
                    gap: "3px",
                    backgroundColor: briefPal.segBg,
                    borderRadius: briefRad.md,
                    padding: "3px"
                  }}
                >
                  <button
                    onClick={() => setTransportMode('driving')}
                    aria-label="Driving"
                    style={{
                      width: "44px",
                      height: "36px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: briefRad.sm,
                      backgroundColor: transportMode === 'driving' ? briefPal.segActive : 'transparent',
                      border: "none",
                      cursor: "pointer",
                      transition: "all 150ms ease"
                    }}
                  >
                    <Car
                      className="h-5 w-5"
                      style={{ color: transportMode === 'driving' ? briefPal.text : briefPal.textMuted }}
                    />
                  </button>
                  <button
                    onClick={() => setTransportMode('transit')}
                    aria-label="Public transport"
                    style={{
                      width: "44px",
                      height: "36px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: briefRad.sm,
                      backgroundColor: transportMode === 'transit' ? briefPal.segActive : 'transparent',
                      border: "none",
                      cursor: "pointer",
                      transition: "all 150ms ease"
                    }}
                  >
                    <Train
                      className="h-5 w-5"
                      style={{ color: transportMode === 'transit' ? briefPal.text : briefPal.textMuted }}
                    />
                  </button>
                </div>

                {/* Leave by Time */}
                <div style={{ textAlign: "right", flex: "1", minWidth: "0" }}>
                  {loadingJourney ? (
                    <Loader2 className="h-4 w-4 animate-spin" style={{ color: briefPal.textMuted, float: "right" }} />
                  ) : journeyInfo?.success && journeyInfo?.leave_by_time ? (
                    <>
                      <div
                        style={{
                          fontFamily: briefFont.body,
                          fontWeight: 700,
                          fontSize: "18px",
                          letterSpacing: "-0.02em",
                          lineHeight: 1,
                          color: briefPal.amber,
                          whiteSpace: "nowrap"
                        }}
                      >
                        Leave by {journeyInfo.leave_by_time}
                      </div>
                      <div
                        style={{
                          fontFamily: briefFont.mono,
                          fontSize: "10.5px",
                          color: briefPal.textMuted,
                          marginTop: "5px",
                          letterSpacing: "0.04em",
                          lineHeight: 1.5,
                        }}
                      >
                        {journeyInfo.train_departure_time ? (
                          <>
                            <div>
                              🚗 {journeyInfo.drive_to_station_minutes}m drive
                              {journeyInfo.station_name ? ` to ${journeyInfo.station_name}` : ""}
                            </div>
                            {/* The timetable is for the GIG's date, not today —
                                tag the weekday when they differ so a Friday
                                train checked on a Tuesday doesn't look made-up. */}
                            <div>
                              🚂 {journeyInfo.train_departure_time} {journeyInfo.transit_vehicle || "train"}
                              {gig.date && !isToday(parseISO(gig.date)) ? ` (${format(parseISO(gig.date), "EEE")})` : ""}
                            </div>
                          </>
                        ) : (
                          <div>{journeyInfo.duration_text}</div>
                        )}
                        <button
                          type="button"
                          onClick={toggleBuffer}
                          style={{
                            marginTop: "3px",
                            background: "none",
                            border: "none",
                            padding: 0,
                            cursor: "pointer",
                            color: applyBuffer ? briefPal.amber : briefPal.textMuted,
                            fontFamily: briefFont.mono,
                            fontSize: "10.5px",
                            letterSpacing: "0.04em",
                            textDecoration: "underline",
                            textUnderlineOffset: "2px",
                          }}
                          aria-label={applyBuffer ? "Buffer applied — click to remove" : "Buffer off — click to apply"}
                        >
                          {applyBuffer
                            ? `+ ${journeyInfo.buffer_minutes || 30} min buffer ✓`
                            : `+ ${journeyInfo.buffer_minutes || 30} min buffer ✕`}
                        </button>
                      </div>
                    </>
                  ) : journeyInfo?.error ? (
                    <div style={{ fontSize: "10.5px", color: briefPal.textMuted }}>
                      {journeyInfo.error}
                    </div>
                  ) : (
                    <div style={{ fontSize: "10.5px", color: briefPal.textMuted }}>
                      {gig.call_time ? "Set home address in Settings" : "Add a call time to calculate"}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {/* Fee */}
          <div style={{ marginBottom: "16px", borderTop: briefBorder.hairline, paddingTop: "12px" }}>
            <div
              style={{
                fontFamily: briefFont.mono,
                fontSize: "9px",
                fontWeight: 600,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: briefPal.textMuted,
                marginBottom: "8px"
              }}
            >
              Fee
            </div>
            {editingField === 'fee' ? (
              <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                <span style={{ color: briefPal.green, fontWeight: 700, fontSize: "16px" }} aria-hidden="true">£</span>
                <Input
                  type="number"
                  inputMode="decimal"
                  step="any"
                  value={editValues.fee}
                  onChange={(e) => setEditValues({ ...editValues, fee: e.target.value })}
                  placeholder="0"
                  autoFocus
                  {...inputEditHandlers('fee')}
                  style={{
                    fontSize: "16px",
                    fontWeight: 700,
                    flex: "1",
                    minWidth: 0,
                    backgroundColor: briefPal.innerBg,
                    borderColor: briefPal.innerBorder,
                    color: briefPal.text,
                  }}
                />
                <CancelXButton />
                {SaveTickButton('fee')}
              </div>
            ) : (
              <button
                onClick={() => setEditingField('fee')}
                style={{
                  fontFamily: briefFont.body,
                  fontSize: "20px",
                  fontWeight: 600,
                  color: gig.fee ? briefPal.green : briefPal.textMuted,
                  backgroundColor: "transparent",
                  border: "none",
                  cursor: "pointer",
                  padding: "4px 0",
                  textAlign: "left",
                }}
              >
                {gig.fee ? `£${gig.fee}` : "—"}
              </button>
            )}
          </div>

          {/* Dress Code — inline single line (always short enough, no dropdown) */}
          <div
            style={{
              borderTop: briefBorder.hairline,
              paddingTop: "12px",
              borderBottom: briefBorder.hairline,
              paddingBottom: "12px"
            }}
          >
            {editingField === 'dress_code' ? (
              <div>
                <div
                  style={{
                    fontFamily: briefFont.mono,
                    fontSize: "9px",
                    fontWeight: 600,
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                    color: briefPal.textMuted,
                    marginBottom: "8px"
                  }}
                >
                  Dress code
                </div>
                <div style={{ display: "flex", gap: "6px" }}>
                  <Input
                    value={editValues.dress_code}
                    onChange={(e) => setEditValues({...editValues, dress_code: e.target.value})}
                    placeholder="e.g., Black tie"
                    autoFocus
                    {...inputEditHandlers('dress_code')}
                    style={{
                      fontFamily: briefFont.body,
                      fontSize: "13px",
                      flex: "1",
                      backgroundColor: briefPal.innerBg,
                      borderColor: briefPal.innerBorder,
                      color: briefPal.text
                    }}
                  />
                  <CancelXButton />
                  {SaveTickButton('dress_code')}
                </div>
              </div>
            ) : dressFitInline ? (
              <button
                onClick={() => setEditingField('dress_code')}
                data-testid="dress-code-row"
                style={{
                  width: "100%",
                  padding: "0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "10px",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  textAlign: "left",
                }}
              >
                <span
                  style={{
                    fontFamily: briefFont.mono,
                    fontSize: "9px",
                    fontWeight: 600,
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                    color: briefPal.textMuted,
                    flexShrink: 0
                  }}
                >
                  Dress code
                </span>
                <span
                  style={{
                    fontFamily: briefFont.body,
                    fontSize: "13px",
                    fontWeight: 500,
                    color: gig.dress_code ? briefPal.text : briefPal.textMuted,
                    textAlign: "right",
                    lineHeight: 1.5,
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap"
                  }}
                >
                  {gig.dress_code || "Add dress code…"}
                </span>
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => setDresscodeOpen(!dresscodeOpen)}
                  aria-expanded={dresscodeOpen}
                  data-testid="dress-code-toggle"
                  style={{
                    width: "100%",
                    padding: "0",
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                    textAlign: "left",
                  }}
                >
                  <span
                    style={{
                      fontFamily: briefFont.mono,
                      fontSize: "9px",
                      fontWeight: 600,
                      letterSpacing: "0.12em",
                      textTransform: "uppercase",
                      color: briefPal.textMuted,
                      flexShrink: 0
                    }}
                  >
                    Dress code
                  </span>
                  {!dresscodeOpen && (
                    <span
                      style={{
                        flex: "1",
                        minWidth: 0,
                        fontFamily: briefFont.body,
                        fontSize: "13px",
                        color: briefPal.textDim,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap"
                      }}
                    >
                      {gig.dress_code}
                    </span>
                  )}
                  <ChevronDown
                    className="h-3.5 w-3.5"
                    style={{
                      color: briefPal.textMuted,
                      flexShrink: 0,
                      marginLeft: "auto",
                      transform: dresscodeOpen ? "rotate(180deg)" : "rotate(0deg)",
                      transition: `transform ${tokens.motion.fast}`,
                    }}
                  />
                </button>
                {dresscodeOpen && (
                  <button
                    onClick={() => setEditingField('dress_code')}
                    style={{
                      fontFamily: briefFont.body,
                      fontSize: "13px",
                      lineHeight: 1.5,
                      color: briefPal.text,
                      textWrap: "pretty",
                      backgroundColor: "transparent",
                      border: "none",
                      cursor: "pointer",
                      width: "100%",
                      textAlign: "left",
                      padding: "12px 0 0",
                      borderRadius: "4px"
                    }}
                  >
                    {gig.dress_code}
                  </button>
                )}
              </>
            )}
          </div>

          {/* Notes — inline when short, dropdown only when too long for one line */}
          <div
            style={{
              borderBottom: gigCollaborators.length > 0 || shareEnabled ? briefBorder.hairline : "none"
            }}
          >
            {editingField === 'gig_notes' ? (
              <div style={{ padding: "12px 0" }}>
                <div
                  style={{
                    fontFamily: briefFont.mono,
                    fontSize: "9px",
                    fontWeight: 600,
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                    color: briefPal.textMuted,
                    marginBottom: "8px"
                  }}
                >
                  Notes
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  <Textarea
                    value={editValues.gig_notes}
                    onChange={(e) => setEditValues({...editValues, gig_notes: e.target.value})}
                    placeholder="Add gig notes..."
                    autoFocus
                    {...inputBlurCommit('gig_notes')}
                    style={{
                      fontSize: "13px",
                      minHeight: "80px",
                      fontFamily: briefFont.body,
                      backgroundColor: briefPal.innerBg,
                      borderColor: briefPal.innerBorder,
                      color: briefPal.text
                    }}
                  />
                  <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
                    <button
                      {...cancelBtnHandlers}
                      aria-label="Cancel"
                      style={{
                        padding: "8px 16px",
                        borderRadius: briefRad.sm,
                        backgroundColor: "transparent",
                        border: `1px solid ${briefPal.innerBorder}`,
                        color: briefPal.textMuted,
                        cursor: "pointer",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        fontWeight: 600,
                        fontSize: "13px",
                      }}
                    >
                      <X className="h-3.5 w-3.5" />
                      Cancel
                    </button>
                    <button
                      {...saveBtnHandlers('gig_notes')}
                      aria-label="Save"
                      style={{
                        padding: "8px 16px",
                        borderRadius: briefRad.sm,
                        backgroundColor: briefPal.orange,
                        border: "none",
                        cursor: "pointer",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        color: briefPal.onOrange,
                        fontWeight: 600,
                        fontSize: "13px",
                      }}
                    >
                      {isSaving ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Check className="h-3.5 w-3.5" />
                      )}
                      Save
                    </button>
                  </div>
                </div>
              </div>
            ) : notesFitInline ? (
              <button
                onClick={() => setEditingField('gig_notes')}
                data-testid="notes-row"
                style={{
                  width: "100%",
                  padding: "12px 0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "10px",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  textAlign: "left",
                }}
              >
                <span
                  style={{
                    fontFamily: briefFont.mono,
                    fontSize: "9px",
                    fontWeight: 600,
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                    color: briefPal.textMuted,
                    flexShrink: 0
                  }}
                >
                  Notes
                </span>
                <span
                  style={{
                    fontFamily: briefFont.body,
                    fontSize: "13px",
                    color: notesOf(gig) ? briefPal.text : briefPal.textMuted,
                    textAlign: "right",
                    lineHeight: 1.5,
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap"
                  }}
                >
                  {notesOf(gig) || "Add notes…"}
                </span>
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => setNotesOpen(!notesOpen)}
                  aria-expanded={notesOpen}
                  data-testid="notes-toggle"
                  style={{
                    width: "100%",
                    padding: "12px 0",
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                    textAlign: "left",
                  }}
                >
                  <div
                    style={{
                      fontFamily: briefFont.mono,
                      fontSize: "9px",
                      fontWeight: 600,
                      letterSpacing: "0.12em",
                      textTransform: "uppercase",
                      color: briefPal.textMuted,
                      flexShrink: 0
                    }}
                  >
                    Notes
                  </div>
                  {!notesOpen && (
                    <span
                      style={{
                        flex: "1",
                        minWidth: 0,
                        fontFamily: briefFont.body,
                        fontSize: "13px",
                        color: briefPal.textDim,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap"
                      }}
                    >
                      {notesOf(gig)}
                    </span>
                  )}
                  <ChevronDown
                    className="h-3.5 w-3.5"
                    style={{
                      color: briefPal.textMuted,
                      flexShrink: 0,
                      marginLeft: "auto",
                      transform: notesOpen ? "rotate(180deg)" : "rotate(0deg)",
                      transition: `transform ${tokens.motion.fast}`,
                    }}
                  />
                </button>
                {notesOpen && (
                  <div style={{ marginTop: "12px", paddingBottom: "12px" }}>
                    <button
                      onClick={() => setEditingField('gig_notes')}
                      style={{
                        fontSize: "13px",
                        lineHeight: 1.5,
                        color: briefPal.textDim,
                        margin: 0,
                        textWrap: "pretty",
                        backgroundColor: "transparent",
                        border: "none",
                        cursor: "pointer",
                        width: "100%",
                        textAlign: "left",
                        padding: "4px 0",
                        borderRadius: "4px"
                      }}
                    >
                      {notesOf(gig)}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Gig Contacts — inline names when few, dropdown only when too many for one line */}
          {gigCollaborators.length > 0 && (
            contactsFitInline ? (
              <div
                style={{
                  padding: "12px 0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "10px",
                }}
              >
                <span
                  style={{
                    fontFamily: briefFont.mono,
                    fontSize: "9px",
                    fontWeight: 600,
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                    color: briefPal.textMuted,
                    flexShrink: 0
                  }}
                >
                  Gig contacts
                </span>
                <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "flex-end", alignItems: "center", gap: "12px", minWidth: 0 }}>
                  {gigCollaborators.map((c, i) => {
                    const gradients = [
                      { from: "#f5891f", to: "#c14a09" },
                      { from: "#7a5af0", to: "#4b2dc7" },
                      { from: "#22c197", to: "#0e7e60" },
                    ];
                    const g = gradients[i % gradients.length];
                    const initials = c.name.split(' ').map(n => n[0]).join('');
                    return (
                      <div key={c.collaborator_id || i} role="button" tabIndex={0} onClick={() => setActiveContact(c)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setActiveContact(c); } }} data-testid={`gig-contact-${c.collaborator_id}`} style={{ display: "flex", alignItems: "center", gap: "6px", minWidth: 0, cursor: "pointer", padding: "6px 2px" }}>
                        <div
                          style={{
                            width: "22px",
                            height: "22px",
                            borderRadius: briefRad.pill,
                            background: `linear-gradient(135deg, ${g.from} 0%, ${g.to} 100%)`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "white",
                            fontWeight: 700,
                            fontSize: "9px",
                            flexShrink: 0
                          }}
                        >
                          {initials}
                        </div>
                        <span style={{ fontFamily: briefFont.body, fontSize: "13px", fontWeight: 600, color: briefPal.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>
                          {c.name}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <ContactsDisclosure
                contacts={gigCollaborators}
                open={withOpen}
                onToggle={() => setWithOpen(!withOpen)}
                onSelect={setActiveContact}
                last
              />
            )
          )}
        </div>

        {/* Trip done — multi-day only; ends the next-action tracker for the trip */}
        {gig.is_multiday && hasPerformanceStarted() && (
          <div style={{ padding: "12px 16px", borderTop: briefBorder.hairline, backgroundColor: briefPal.appBg }}>
            <button
              onClick={toggleTripDone}
              disabled={finishingTrip}
              style={{
                width: "100%",
                padding: "10px 12px",
                background: gig.gig_finished ? briefPal.green : briefPal.innerBg,
                color: gig.gig_finished ? briefPal.onGreen : briefPal.text,
                border: "none",
                borderRadius: briefRad.md,
                fontFamily: briefFont.body,
                fontSize: "13px",
                fontWeight: 600,
                cursor: finishingTrip ? "default" : "pointer",
                transition: `background ${tokens.motion.fast}, color ${tokens.motion.fast}`,
              }}
            >
              {finishingTrip ? "Saving…" : gig.gig_finished ? "✓ Trip done" : "Mark trip done"}
            </button>
          </div>
        )}

        {/* Share Footer */}
        <div
          style={{
            padding: "16px",
            borderTop: briefBorder.hairline,
            backgroundColor: briefPal.appBg
          }}
        >
          {/* Share actions: each opens a WhatsApp / Text choice. Pressing one means you want to share, so there is no on/off switch. */}
          {gig?.gig_id && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
              <button
                onClick={() => setShareKind("dep")}
                data-testid="dep-this-out"
                style={{
                  border: briefBorder.card,
                  borderRadius: briefRad.md + 1,
                  padding: "14px 12px",
                  minHeight: "48px",
                  backgroundColor: briefPal.orange,
                  color: "white",
                  fontFamily: briefFont.body,
                  fontWeight: 600,
                  fontSize: "14px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px"
                }}
              >
                <MessageCircle className="h-3.5 w-3.5" style={{ color: "white" }} />
                Dep this out
              </button>
              <button
                onClick={() => setShareKind("family")}
                data-testid="share-gig"
                style={{
                  border: briefBorder.card,
                  borderRadius: briefRad.md + 1,
                  padding: "14px 12px",
                  minHeight: "48px",
                  backgroundColor: "transparent",
                  color: briefPal.orange,
                  fontFamily: briefFont.body,
                  fontWeight: 600,
                  fontSize: "14px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px"
                }}
              >
                <MessageCircle className="h-3.5 w-3.5" style={{ color: briefPal.orange }} />
                Share gig
              </button>
            </div>
          )}
          <GigShareDialog
            gig={{ ...gig, share_enabled: shareEnabled, share_token: shareToken }}
            kind={shareKind || "family"}
            open={!!shareKind}
            onClose={() => setShareKind(null)}
            onPublicEnabled={(token) => { setShareEnabled(true); setShareToken(token); }}
          />
          {shareEnabled && gig?.gig_id && (
            <div style={{ marginTop: "12px", textAlign: "center", fontFamily: briefFont.body, fontSize: "12px", color: briefPal.textMuted }}>
              Public link is on ·{" "}
              <button
                type="button"
                onClick={handleShareToggle}
                data-testid="stop-sharing"
                style={{ background: "none", border: "none", padding: "8px 4px", color: briefPal.orange, fontFamily: briefFont.body, fontSize: "12px", textDecoration: "underline", cursor: "pointer" }}
              >
                Stop sharing
              </button>
            </div>
          )}
          <ContactSheet contact={activeContact} onClose={() => setActiveContact(null)} />
        </div>

        <style>{`
          @keyframes pulse {
            0%, 100% { opacity: 0.8; }
            50% { opacity: 0.2; }
          }
        `}</style>
      </div>
    </div>
  );
};

function TimeRow({ icon: Icon, label, value, iconColor, valueColor, bold }) {
  return (
    <div style={{ display: "flex", alignItems: "center", padding: "6px 0", fontSize: "13.5px", gap: "10px" }}>
      <Icon className="h-3.75 w-3.75 flex-shrink-0" style={{ color: iconColor }} />
      <span style={{ color: briefPal.textMuted, flex: "1", overflow: "hidden", textOverflow: "ellipsis" }}>
        {label}
      </span>
      <span
        style={{
          color: valueColor || briefPal.text,
          fontWeight: bold ? 600 : 500,
          whiteSpace: "nowrap",
          textAlign: "right"
        }}
      >
        {value}
      </span>
    </div>
  );
}

function NotesDisclosure({ open, onToggle, preview, last, isEditing, onEditClick, isSaving, inputBlurCancel, saveBtnHandlers, cancelBtnHandlers, value, onChangeValue, fullContent }) {
  return (
    <div style={{ borderBottom: last ? "none" : briefBorder.hairline }}>
      <button
        onClick={onToggle}
        style={{
          width: "100%",
          padding: "14px 16px",
          display: "flex",
          alignItems: "center",
          gap: "10px",
          textAlign: "left",
          backgroundColor: "transparent",
          border: "none",
          cursor: "pointer",
          borderRadius: "0"
        }}
      >
        <span
          style={{
            fontFamily: briefFont.mono,
            fontSize: "10px",
            fontWeight: 600,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: briefPal.textMuted,
            flexShrink: 0
          }}
        >
          NOTES
        </span>
        {!open && preview && (
          <span
            style={{
              flex: "1",
              minWidth: 0,
              fontSize: "12.5px",
              color: briefPal.textDim,
              textAlign: "right",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap"
            }}
          >
            {preview}
          </span>
        )}
        <ChevronDown
          className="h-3.5 w-3.5 flex-shrink-0"
          style={{
            color: briefPal.textMuted,
            transition: "transform 180ms ease",
            transform: open ? "rotate(180deg)" : "rotate(0deg)"
          }}
        />
      </button>
      {open && (
        <>
          <div style={{ borderBottom: briefBorder.hairline }} />
          <div style={{ padding: "14px 16px" }}>
            {isEditing ? (
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                <Textarea
                  value={value}
                  onChange={(e) => onChangeValue(e.target.value)}
                  placeholder="Add gig notes..."
                  autoFocus
                  {...inputBlurCancel}
                  style={{
                    fontSize: "13px",
                    minHeight: "80px",
                    fontFamily: briefFont.body,
                    backgroundColor: briefPal.innerBg,
                    borderColor: briefPal.innerBorder,
                    color: briefPal.text
                  }}
                />
                <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
                  <button
                    {...cancelBtnHandlers}
                    aria-label="Cancel"
                    style={{
                      padding: "8px 16px",
                      borderRadius: briefRad.sm,
                      backgroundColor: "transparent",
                      border: `1px solid ${briefPal.innerBorder}`,
                      color: briefPal.textMuted,
                      cursor: "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      fontWeight: 600,
                      fontSize: "13px",
                    }}
                  >
                    <X className="h-3.5 w-3.5" />
                    Cancel
                  </button>
                  <button
                    {...saveBtnHandlers}
                    aria-label="Save"
                    style={{
                      padding: "8px 16px",
                      borderRadius: briefRad.sm,
                      backgroundColor: briefPal.orange,
                      border: "none",
                      cursor: "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      color: briefPal.onOrange,
                      fontWeight: 600,
                      fontSize: "13px",
                    }}
                  >
                    {isSaving ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Check className="h-3.5 w-3.5" />
                    )}
                    Save
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={onEditClick}
                style={{
                  fontSize: "13px",
                  lineHeight: 1.5,
                  color: briefPal.textDim,
                  margin: 0,
                  textWrap: "pretty",
                  backgroundColor: "transparent",
                  border: "none",
                  cursor: "pointer",
                  width: "100%",
                  textAlign: "left",
                  padding: "4px",
                  borderRadius: "4px"
                }}
              >
                {fullContent}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function ContactsDisclosure({ contacts, open, onToggle, onSelect, last }) {
  return (
    <div style={{ borderBottom: last ? "none" : briefBorder.hairline }}>
      <button
        onClick={onToggle}
        style={{
          width: "100%",
          padding: "14px 16px",
          display: "flex",
          alignItems: "center",
          gap: "10px",
          textAlign: "left",
          backgroundColor: "transparent",
          border: "none",
          cursor: "pointer",
          borderRadius: "0"
        }}
      >
        <span
          style={{
            fontFamily: briefFont.mono,
            fontSize: "10px",
            fontWeight: 600,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: briefPal.textMuted,
            flexShrink: 0
          }}
        >
          GIG CONTACTS
        </span>
        {!open && (
          <div style={{ flex: "1", display: "flex", justifyContent: "flex-end" }}>
            <AvatarStack contacts={contacts} />
          </div>
        )}
        <ChevronDown
          className="h-3.5 w-3.5 flex-shrink-0"
          style={{
            color: briefPal.textMuted,
            transition: "transform 180ms ease",
            transform: open ? "rotate(180deg)" : "rotate(0deg)"
          }}
        />
      </button>
      {open && (
        <>
          <div style={{ borderBottom: briefBorder.hairline }} />
          <div style={{ padding: "4px 16px 0" }}>
            <PeopleList contacts={contacts} onSelect={onSelect} />
          </div>
        </>
      )}
    </div>
  );
}

function AvatarStack({ contacts }) {
  const gradients = [
    { from: "#f5891f", to: "#c14a09" }, // orange
    { from: "#7a5af0", to: "#4b2dc7" }, // purple
    { from: "#22c197", to: "#0e7e60" }, // green
  ];

  return (
    <div style={{ display: "flex", alignItems: "center" }}>
      {contacts.slice(0, 3).map((c, i) => {
        const initials = c.name.split(' ').map(n => n[0]).join('');
        const gradient = gradients[i % gradients.length];
        return (
          <div
            key={i}
            style={{
              width: "26px",
              height: "26px",
              borderRadius: briefRad.pill,
              background: `linear-gradient(135deg, ${gradient.from} 0%, ${gradient.to} 100%)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontWeight: 700,
              fontSize: "9.5px",
              border: "2px solid",
              borderColor: briefPal.cardBg,
              marginLeft: i === 0 ? 0 : "-8px",
              zIndex: 10 - i
            }}
          >
            {initials}
          </div>
        );
      })}
    </div>
  );
}

function PeopleList({ contacts, onSelect }) {
  const gradients = [
    { from: "#f5891f", to: "#c14a09" }, // orange
    { from: "#7a5af0", to: "#4b2dc7" }, // purple
    { from: "#22c197", to: "#0e7e60" }, // green
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "10px", paddingTop: "4px" }}>
      {contacts.map((c, i) => {
        const initials = c.name.split(' ').map(n => n[0]).join('');
        const gradient = gradients[i % gradients.length];
        return (
          <div key={i} role={onSelect ? "button" : undefined} tabIndex={onSelect ? 0 : undefined} onClick={onSelect ? () => onSelect(c) : undefined} style={{ display: "flex", alignItems: "center", gap: "12px", cursor: onSelect ? "pointer" : "default", padding: "4px 0" }}>
            <div
              style={{
                width: "30px",
                height: "30px",
                borderRadius: briefRad.pill,
                background: `linear-gradient(135deg, ${gradient.from} 0%, ${gradient.to} 100%)`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                fontWeight: 700,
                fontSize: "11px",
                flexShrink: 0
              }}
            >
              {initials}
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div
                style={{
                  fontFamily: briefFont.body,
                  fontWeight: 600,
                  fontSize: "13.5px",
                  color: briefPal.text,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap"
                }}
              >
                {c.name}
              </div>
              <div
                style={{
                  fontFamily: briefFont.body,
                  fontSize: "11.5px",
                  color: briefPal.textMuted,
                  marginTop: "2px"
                }}
              >
                {c.instrument || c.role || 'Musician'}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default GigBrief;
